import { create } from "zustand";
import { persist } from "zustand/middleware";
import { api } from "../lib/api";

const useStore = create(
  persist(
    (set, get) => ({
      // server-cached
      apiShows: [],
      apiShowsLoaded: false,
      savedShows: [], // string[] of showIds, synced from server

      // still local: custom shows (admin can curate via real api now too,
      // this stays local only as a fallback "i'm at a show that's not in
      // the db yet" capture).
      customShows: [],

      // ---------- server state lifecycle ----------
      loadServerState: async () => {
        try {
          const [showsRes, savesRes] = await Promise.all([
            api.get("/shows"),
            api.get("/me/saves"),
          ]);
          set({
            apiShows: showsRes.data.items || [],
            apiShowsLoaded: true,
            savedShows: (savesRes.data.items || []).map((x) => x.id),
          });
        } catch (err) {
          console.warn("loadServerState failed:", err);
          set({ apiShowsLoaded: true });
        }
      },

      refreshSaves: async () => {
        try {
          const { data } = await api.get("/me/saves");
          set({ savedShows: (data.items || []).map((x) => x.id) });
        } catch (err) {
          console.warn("refreshSaves failed:", err);
        }
      },

      clearServerState: () =>
        set({
          apiShows: [],
          apiShowsLoaded: false,
          savedShows: [],
          customShows: [],
        }),

      // ---------- saves ----------
      toggleSave: async (showId) => {
        const isSaved = get().savedShows.includes(showId);
        set((s) => ({
          savedShows: isSaved
            ? s.savedShows.filter((id) => id !== showId)
            : [showId, ...s.savedShows],
        }));
        try {
          if (isSaved) await api.delete(`/shows/${showId}/save`);
          else await api.post(`/shows/${showId}/save`);
        } catch {
          set((s) => ({
            savedShows: isSaved
              ? [showId, ...s.savedShows]
              : s.savedShows.filter((id) => id !== showId),
          }));
        }
      },

      removeSave: async (showId) => {
        set((s) => ({ savedShows: s.savedShows.filter((id) => id !== showId) }));
        try {
          await api.delete(`/shows/${showId}/save`);
        } catch (err) {
          console.warn("removeSave failed:", err);
        }
      },

      isSaved: (showId) => get().savedShows.includes(showId),

      // ---------- custom shows (local — out of scope for alpha api) ----------
      addShow: (show) => {
        const baseSlug = (show.title || "untitled")
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-|-$/g, "")
          .slice(0, 40) || "show";
        const id = `custom-${baseSlug}-${Date.now().toString(36)}`;
        const record = {
          id,
          title: show.title || "untitled",
          theatre: show.theatre || "",
          venue: show.venue || show.theatre || "",
          company: show.company || show.theatre || "",
          dateLabel: show.dateLabel || "",
          startDate: show.startDate || null,
          endDate: show.endDate || null,
          status: show.status || "current",
          city: (show.city || "toronto").toLowerCase(),
          categories: show.categories || ["custom"],
          playwright: show.playwright || null,
          creator: show.creator || show.playwright || null,
          director: show.director || null,
          cast: show.cast || [],
          creativeTeam: show.creativeTeam || [],
          teamStatus: show.teamStatus || "user-added",
          sourceUrl: show.sourceUrl || "",
          description: show.description || "",
          posterUrl: show.posterUrl || null,
          size: show.size || "indie",
          custom: true,
        };
        set((s) => ({ customShows: [record, ...s.customShows] }));
        return id;
      },
    }),
    {
      name: "the-lobby-store",
      version: 5,
      // server-cached fields stay out of localStorage
      partialize: (state) => ({
        customShows: state.customShows,
      }),
      migrate: () => ({
        customShows: [],
      }),
    }
  )
);

export default useStore;
